// The language picker of the cabinet's header and the landing's: the current code, and a short list of the codes on
// offer. What the MudSelect drew there.

import { h } from '../dom.ts';

/**
 * @param {{ value: string | null, locales: Array<{ name: string }>, onChange: (name: string) => void, label?: string }} o
 */
export function languageSelector(o: { value: string | null; locales: Array<{ name: string; }>; onChange: (name: string) => void; label?: string; }) {
    let open = false;
    let active = -1;

    const trigger = h('button', {
        type: 'button',
        class: 'lang-trigger',
        'aria-haspopup': 'listbox',
        'aria-expanded': 'false',
        'aria-label': o.label || 'Language'
    }, (o.value || '').toUpperCase());

    const list = h('ul', { class: 'lang-list', role: 'listbox', tabindex: -1, hidden: true });
    const root = h('div', { class: 'language-selector' }, trigger, list);

    function drawList() {
        list.replaceChildren(...o.locales.map((locale, index) => h('li', {
            class: 'lang-option' + (locale.name === o.value ? ' selected' : '') + (index === active ? ' active' : ''),
            role: 'option',
            'aria-selected': locale.name === o.value ? 'true' : 'false',
            onpointerdown: (event: PointerEvent) => event.preventDefault(),
            onclick: () => choose(index)
        }, locale.name.toUpperCase())));
    }

    function outside(event: Event): void {
        const target = event.target as Node | null;

        if (!root.contains(target) && !list.contains(target)) close(false);
    }

    // The list hangs from where the trigger was; once the page moves under it, it closes rather than float away.
    function moved(event: Event): void {
        if (event.type === 'resize' || !list.contains(event.target as Node | null)) close(false);
    }

    function show() {
        if (open || !o.locales.length) return;

        const anchor = trigger.getBoundingClientRect();

        open = true;
        active = Math.max(0, o.locales.findIndex(locale => locale.name === o.value));
        drawList();
        list.style.left = `${anchor.left + anchor.width / 2}px`;
        list.style.top = `${anchor.bottom + 8}px`;
        document.body.append(list);
        list.hidden = false;
        root.classList.add('open');
        trigger.setAttribute('aria-expanded', 'true');
        list.focus({ preventScroll: true });
        document.addEventListener('pointerdown', outside, true);
        document.addEventListener('scroll', moved, true);
        window.addEventListener('resize', moved);
    }

    function close(focusTrigger = true) {
        if (!open) return;

        open = false;
        list.hidden = true;
        root.append(list);
        root.classList.remove('open');
        trigger.setAttribute('aria-expanded', 'false');
        document.removeEventListener('pointerdown', outside, true);
        document.removeEventListener('scroll', moved, true);
        window.removeEventListener('resize', moved);

        if (focusTrigger && trigger.isConnected) trigger.focus({ preventScroll: true });
    }

    function choose(index: number): void {
        const locale = o.locales[index];

        close();

        if (locale && locale.name !== o.value) o.onChange(locale.name);
    }

    trigger.addEventListener('click', () => (open ? close() : show()));
    trigger.addEventListener('keydown', event => {
        if (['ArrowDown', 'Enter', ' '].includes(event.key)) {
            event.preventDefault();
            show();
        }
    });
    list.addEventListener('keydown', event => {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            active = Math.max(0, Math.min(o.locales.length - 1, active + (event.key === 'ArrowDown' ? 1 : -1)));
            drawList();
        } else if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            choose(active);
        } else if (event.key === 'Escape') {
            event.preventDefault();
            close();
        } else if (event.key === 'Tab') {
            close(false);
        }
    });

    return root;
}
